import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../config/api';
import { sortRegions } from '../utils/sort';
import PageLayout from '../components/PageLayout';
import './IngestionPage.css';

interface IngestionTriggerRequest {
  ingestion_type: 'venues' | 'events' | 'events-etix' | 'events-eventim' | 'events-scraper';
  payload?: Record<string, any>;
}

interface IngestionTriggerResponse {
  success: boolean;
  message: string;
  function_name?: string;
}

const IngestionPage: React.FC = () => {
  const { token } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [isTriggerSectionExpanded, setIsTriggerSectionExpanded] = useState(true);

  // Regions state
  const [regions, setRegions] = useState<Array<{
    region_name: string;
    metro_name: string;
    latitude: number;
    longitude: number;
    radius: number;
    unit: string;
    zip_code?: string;
  }>>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [venuesRegionMode, setVenuesRegionMode] = useState<'region' | 'custom' | 'empty'>('empty');
  const [venuesMode, setVenuesMode] = useState<'location' | 'direct_lookup'>('location');
  const [selectedVenuesRegion, setSelectedVenuesRegion] = useState<string>('');
  const [eventsMode, setEventsMode] = useState<'all_venues_by_id' | 'region_venues_by_id' | 'region_by_location'>('all_venues_by_id');
  const [selectedEventsRegion, setSelectedEventsRegion] = useState<string>('');
  const [eventsEtixRegionMode, setEventsEtixRegionMode] = useState<'region' | 'custom' | 'empty'>('empty');
  const [selectedEventsEtixRegion, setSelectedEventsEtixRegion] = useState<string>('');
  const [eventsEventimRegionMode, setEventsEventimRegionMode] = useState<'region' | 'custom' | 'empty'>('empty');
  const [selectedEventsEventimRegion, setSelectedEventsEventimRegion] = useState<string>('');

  // Events scraper state (config list from API)
  const [scraperConfigIds, setScraperConfigIds] = useState<string[]>([]);
  const [loadingScraperConfigs, setLoadingScraperConfigs] = useState(false);
  const [selectedScraperConfig, setSelectedScraperConfig] = useState<string>('');

  // Form state for each ingestion type
  const [venuesPayload, setVenuesPayload] = useState({
    latitude: '',
    longitude: '',
    radius: '',
    unit: 'miles',
    region_name: '',
    page: '0',
  });

  const [eventsPayload, setEventsPayload] = useState({
    latitude: '',
    longitude: '',
    radius: '',
    unit: 'miles',
    start_date: '',
    end_date: '',
    page: '0',
  });

  const [eventsEtixPayload, setEventsEtixPayload] = useState({
    etix_api_type: 'both' as 'both' | 'affiliate' | 'public',
    zip_code: '',
    radius: '',
    latitude: '',
    longitude: '',
    page_number: '1',
    region_name: '',
  });

  const [eventsEventimPayload, setEventsEventimPayload] = useState({
    region_name: '',
  });

  const loadRegions = async () => {
    if (!token) return;
    try {
      setLoadingRegions(true);
      const response = await apiRequest<{ regions: Array<{
        region_name: string;
        metro_name: string;
        latitude: number;
        longitude: number;
        radius: number;
        unit: string;
        zip_code?: string;
      }>; count: number }>('/admin/regions', token);
      setRegions(sortRegions(response.regions || []));
    } catch (err: any) {
      setError(err.message || 'Failed to load regions');
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
  };


  const handleVenuesModalOpen = () => {
    setShowModal('venues');
    loadRegions();
    // Reset to empty mode when opening
    setVenuesRegionMode('empty');
    setVenuesMode('location');
    setSelectedVenuesRegion('');
    setVenuesPayload({
      latitude: '',
      longitude: '',
      radius: '',
      unit: 'miles',
      region_name: '',
      page: '0',
    });
  };

  const handleVenuesRegionChange = (value: string) => {
    if (value === 'custom') {
      setVenuesRegionMode('custom');
      setSelectedVenuesRegion('');
      setVenuesPayload({
        latitude: '',
        longitude: '',
        radius: '',
        unit: 'miles',
        region_name: '',
        page: '0',
      });
    } else if (value === 'empty') {
      setVenuesRegionMode('empty');
      setSelectedVenuesRegion('');
      setVenuesPayload({
        latitude: '',
        longitude: '',
        radius: '',
        unit: 'miles',
        region_name: '',
        page: '0',
      });
    } else {
      // Region selected
      const region = regions.find(r => r.region_name === value);
      if (region) {
        setVenuesRegionMode('region');
        setSelectedVenuesRegion(value);
        setVenuesPayload({
          latitude: region.latitude.toString(),
          longitude: region.longitude.toString(),
          radius: region.radius.toString(),
          unit: region.unit,
          region_name: region.region_name,
          page: '0',
        });
      }
    }
  };

  const handleEventsModalOpen = () => {
    setShowModal('events');
    loadRegions();
    // Reset to default mode when opening
    setEventsMode('all_venues_by_id');
    setSelectedEventsRegion('');
    setEventsPayload({
      latitude: '',
      longitude: '',
      radius: '',
      unit: 'miles',
      start_date: getTodayDate(),
      end_date: getFutureDate(30),
      page: '0',
    });
  };

  const handleEventsModeChange = (mode: 'all_venues_by_id' | 'region_venues_by_id' | 'region_by_location') => {
    setEventsMode(mode);
    if (mode === 'region_by_location' && selectedEventsRegion) {
      // Pre-populate location fields from selected region
      const region = regions.find(r => r.region_name === selectedEventsRegion);
      if (region) {
        setEventsPayload({
          ...eventsPayload,
          latitude: region.latitude.toString(),
          longitude: region.longitude.toString(),
          radius: region.radius.toString(),
          unit: region.unit,
        });
      }
    }
  };

  const handleEventsRegionChange = (value: string) => {
    setSelectedEventsRegion(value);
    if (eventsMode === 'region_by_location') {
      // Pre-populate location fields from selected region
      const region = regions.find(r => r.region_name === value);
      if (region) {
        setEventsPayload({
          ...eventsPayload,
          latitude: region.latitude.toString(),
          longitude: region.longitude.toString(),
          radius: region.radius.toString(),
          unit: region.unit,
        });
      }
    }
  };

  const handleEventsEtixModalOpen = () => {
    setShowModal('events-etix');
    loadRegions();
    // Reset to empty mode when opening
    setEventsEtixRegionMode('empty');
    setSelectedEventsEtixRegion('');
    setEventsEtixPayload({
      etix_api_type: 'both',
      zip_code: '',
      radius: '',
      latitude: '',
      longitude: '',
      page_number: '1',
      region_name: '',
    });
  };

  const handleEventsEtixRegionChange = (value: string) => {
    if (value === 'custom') {
      setEventsEtixRegionMode('custom');
      setSelectedEventsEtixRegion('');
      setEventsEtixPayload({
        ...eventsEtixPayload,
        zip_code: '',
        radius: '',
        latitude: '',
        longitude: '',
        page_number: '1',
        region_name: '',
      });
    } else if (value === 'empty') {
      setEventsEtixRegionMode('empty');
      setSelectedEventsEtixRegion('');
      setEventsEtixPayload({
        ...eventsEtixPayload,
        zip_code: '',
        radius: '',
        latitude: '',
        longitude: '',
        page_number: '1',
        region_name: '',
      });
    } else {
      // Region selected
      const region = regions.find(r => r.region_name === value);
      if (region) {
        setEventsEtixRegionMode('region');
        setSelectedEventsEtixRegion(value);
        setEventsEtixPayload({
          ...eventsEtixPayload,
          zip_code: region.zip_code || '',
          radius: region.radius.toString(),
          latitude: region.latitude.toString(),
          longitude: region.longitude.toString(),
          page_number: '1',
          region_name: region.region_name,
        });
      }
    }
  };

  const triggerIngestion = async (type: 'venues' | 'events' | 'events-etix' | 'events-eventim' | 'events-scraper', payload?: Record<string, any>) => {
    if (!token) return;
    
    setTriggering(true);
    setError('');
    setSuccess('');

    try {
      const request: IngestionTriggerRequest = {
        ingestion_type: type,
        payload: payload || {},
      };

      const response = await apiRequest<IngestionTriggerResponse>(
        '/admin/ingestion/trigger',
        token,
        {
          method: 'POST',
          body: JSON.stringify(request),
        }
      );

      if (response.success) {
        setSuccess(response.message);
        setShowModal(null);
      } else {
        setError(response.message || 'Failed to trigger ingestion');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to trigger ingestion');
    } finally {
      setTriggering(false);
    }
  };

  const handleVenuesSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Build payload based on mode
    const payload: Record<string, any> = {};

    if (venuesMode === 'direct_lookup') {
      payload.ingest_mode = 'direct_lookup';
      // If a specific region is selected, send it. Otherwise it will default to 'chicago' in backend if omitted,
      // or we can enforce selecting a region. The backend code handles region_name.
      if (venuesPayload.region_name) {
        payload.region_name = venuesPayload.region_name;
      }
    } else {
      // Location mode (default)
      payload.ingest_mode = 'location';
      
      // If empty mode, trigger with no payload (or minimal)
      if (venuesRegionMode === 'empty') {
        // Just send empty payload to trigger region lookup behavior in backend
        triggerIngestion('venues');
        return;
      }
      
      if (venuesPayload.latitude) payload.latitude = parseFloat(venuesPayload.latitude);
      if (venuesPayload.longitude) payload.longitude = parseFloat(venuesPayload.longitude);
      if (venuesPayload.radius) payload.radius = parseInt(venuesPayload.radius);
      if (venuesPayload.unit) payload.unit = venuesPayload.unit;
      if (venuesPayload.region_name) payload.region_name = venuesPayload.region_name;
      if (venuesPayload.page) payload.page = parseInt(venuesPayload.page);
    }

    triggerIngestion('venues', Object.keys(payload).length > 0 ? payload : undefined);
  };

  const handleEventsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let payload: Record<string, any> | undefined = undefined;
    
    if (eventsMode === 'all_venues_by_id') {
      // Empty payload triggers venue ID mode for all regions
      payload = undefined;
    } else if (eventsMode === 'region_venues_by_id') {
      // Region name only triggers venue ID mode for that specific region
      if (!selectedEventsRegion) {
        setError('Please select a region');
        return;
      }
      payload = { region_name: selectedEventsRegion };
    } else if (eventsMode === 'region_by_location') {
      // Geo coordinates trigger location-based mode
      if (!eventsPayload.latitude || !eventsPayload.longitude || !eventsPayload.radius) {
        setError('Please provide latitude, longitude, and radius for location-based mode');
        return;
      }
      payload = {
        latitude: parseFloat(eventsPayload.latitude),
        longitude: parseFloat(eventsPayload.longitude),
        radius: parseInt(eventsPayload.radius),
        unit: eventsPayload.unit || 'miles',
      };
      if (eventsPayload.start_date) payload.start_date = eventsPayload.start_date;
      if (eventsPayload.end_date) payload.end_date = eventsPayload.end_date;
      if (eventsPayload.page) payload.page = parseInt(eventsPayload.page);
    }
    
    triggerIngestion('events', payload);
  };

  const handleEventsEtixSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If empty mode, trigger with no payload
    if (eventsEtixRegionMode === 'empty') {
      triggerIngestion('events-etix');
      return;
    }
    
    // Otherwise, build payload from form
    const payload: Record<string, any> = {};
    
    // Add etix_api_type to payload
    payload.etix_api_type = eventsEtixPayload.etix_api_type;
    
    // Add fields based on API type
    if (eventsEtixPayload.etix_api_type === 'public') {
      // Public API uses latitude/longitude
      if (eventsEtixPayload.latitude) payload.latitude = parseFloat(eventsEtixPayload.latitude);
      if (eventsEtixPayload.longitude) payload.longitude = parseFloat(eventsEtixPayload.longitude);
      if (eventsEtixPayload.radius) payload.radius = parseInt(eventsEtixPayload.radius);
    } else {
      // Affiliate API or both use zip_code/radius
      if (eventsEtixPayload.zip_code) payload.zip_code = eventsEtixPayload.zip_code;
      if (eventsEtixPayload.radius) payload.radius = parseInt(eventsEtixPayload.radius);
    }
    
    if (eventsEtixPayload.page_number) payload.page_number = parseInt(eventsEtixPayload.page_number);
    if (eventsEtixPayload.region_name) payload.region_name = eventsEtixPayload.region_name;

    triggerIngestion('events-etix', Object.keys(payload).length > 0 ? payload : undefined);
  };

  const handleEventsEventimModalOpen = () => {
    setShowModal('events-eventim');
    loadRegions();
    setError('');
    setSuccess('');
    // Reset form state
    setEventsEventimRegionMode('empty');
    setSelectedEventsEventimRegion('');
    setEventsEventimPayload({
      region_name: '',
    });
  };

  const handleEventsEventimRegionChange = (value: string) => {
    if (value === 'empty') {
      setEventsEventimRegionMode('empty');
      setSelectedEventsEventimRegion('');
      setEventsEventimPayload({
        region_name: '',
      });
    } else if (value === 'custom') {
      setEventsEventimRegionMode('custom');
      setSelectedEventsEventimRegion('');
      setEventsEventimPayload({
        region_name: '',
      });
    } else {
      // Selected a region
      const region = regions.find((r) => r.region_name === value);
      if (region) {
        setEventsEventimRegionMode('region');
        setSelectedEventsEventimRegion(value);
        setEventsEventimPayload({
          region_name: region.region_name,
        });
      }
    }
  };

  const handleEventsEventimSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If empty mode, trigger with no payload
    if (eventsEventimRegionMode === 'empty') {
      triggerIngestion('events-eventim');
      return;
    }
    
    // Otherwise, build payload from form
    const payload: Record<string, any> = {};
    
    if (eventsEventimPayload.region_name) payload.region_name = eventsEventimPayload.region_name;

    triggerIngestion('events-eventim', Object.keys(payload).length > 0 ? payload : undefined);
  };

  const loadScraperConfigs = async () => {
    if (!token) return;
    setLoadingScraperConfigs(true);
    setError('');
    try {
      const response = await apiRequest<{ config_ids: string[] }>('/admin/ingestion/scraper-configs', token);
      setScraperConfigIds(response.config_ids || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load scraper configs');
      setScraperConfigIds([]);
    } finally {
      setLoadingScraperConfigs(false);
    }
  };

  const handleEventsScraperModalOpen = () => {
    setShowModal('events-scraper');
    loadScraperConfigs();
    setSelectedScraperConfig('');
    setError('');
    setSuccess('');
  };

  const handleEventsScraperRunAll = () => {
    triggerIngestion('events-scraper', {});
  };

  const handleEventsScraperRunOneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedScraperConfig) {
      setError('Please select a config');
      return;
    }
    triggerIngestion('events-scraper', { config_type: selectedScraperConfig });
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getFutureDate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  return (
    <PageLayout title="Ingestion">
      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}
      
      <div className="ingestion-controls">
        <div className="ingestion-triggers">
          <div className="ingestion-triggers-header">
            <h3>Trigger Ingestion</h3>
            <button
              className="collapse-toggle"
              onClick={() => setIsTriggerSectionExpanded(!isTriggerSectionExpanded)}
              aria-label={isTriggerSectionExpanded ? 'Collapse' : 'Expand'}
            >
              {isTriggerSectionExpanded ? '−' : '+'}
            </button>
          </div>
          
          {isTriggerSectionExpanded && (
            <div className="ingestion-triggers-content">
              {/* Ticketmaster Section */}
              <div className="ingestion-section">
                <h4 className="section-title">Ticketmaster</h4>
                <div className="ingestion-row">
                  <button 
                    onClick={handleVenuesModalOpen} 
                    className="trigger-btn trigger-btn-venues"
                  >
                    Venues
                  </button>
                  <div className="ingestion-details-placeholder"></div>
                </div>
                <div className="ingestion-row">
                  <button 
                    onClick={handleEventsModalOpen} 
                    className="trigger-btn trigger-btn-events"
                  >
                    Events
                  </button>
                  <div className="ingestion-details-placeholder"></div>
                </div>
              </div>

              {/* Etix Section */}
              <div className="ingestion-section">
                <h4 className="section-title">Etix</h4>
                <div className="ingestion-row">
                  <button 
                    onClick={handleEventsEtixModalOpen} 
                    className="trigger-btn trigger-btn-events"
                  >
                    Events
                  </button>
                  <div className="ingestion-details-placeholder"></div>
                </div>
              </div>

              {/* EventIM Section */}
              <div className="ingestion-section">
                <h4 className="section-title">EventIM</h4>
                <div className="ingestion-row">
                  <button 
                    onClick={handleEventsEventimModalOpen} 
                    className="trigger-btn trigger-btn-events"
                  >
                    Events
                  </button>
                  <div className="ingestion-details-placeholder"></div>
                </div>
              </div>

              {/* Events Scraper Section */}
              <div className="ingestion-section">
                <h4 className="section-title">Events Scraper</h4>
                <div className="ingestion-row">
                  <button
                    onClick={handleEventsScraperRunAll}
                    disabled={triggering}
                    className="trigger-btn trigger-btn-events"
                  >
                    Run all
                  </button>
                  <button
                    onClick={handleEventsScraperModalOpen}
                    className="trigger-btn trigger-btn-events"
                  >
                    Run one
                  </button>
                  <div className="ingestion-details-placeholder"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Venues Ingestion Modal */}
      {showModal === 'venues' && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trigger Ticketmaster Venues Ingestion</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>
            <form onSubmit={handleVenuesSubmit}>
              <div className="form-group">
                <label>Ingestion Mode</label>
                <select
                  value={venuesMode}
                  onChange={(e) => setVenuesMode(e.target.value as 'location' | 'direct_lookup')}
                >
                  <option value="location">Location Search (Default)</option>
                  <option value="direct_lookup">Direct Lookup (Allowlist IDs)</option>
                </select>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  {venuesMode === 'location' ? (
                    <span>Finds venues by searching within a radius of a location. Good for discovery but may miss some venues.</span>
                  ) : (
                    <span>Directly queries venues by ID from the Allowlist. Guarantees finding all allowlisted venues (e.g. Empty Bottle aliases).</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Region</label>
                <select
                  value={venuesRegionMode === 'region' ? selectedVenuesRegion : venuesRegionMode}
                  onChange={(e) => handleVenuesRegionChange(e.target.value)}
                  disabled={loadingRegions}
                >
                  {loadingRegions ? (
                    <option>Loading regions...</option>
                  ) : (
                    <>
                      {regions.map((region) => (
                        <option key={region.region_name} value={region.region_name}>
                          {region.metro_name} ({region.region_name})
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                      <option value="empty">Empty</option>
                    </>
                  )}
                </select>
              </div>
              {venuesRegionMode !== 'empty' && (
                <>
                  {venuesMode === 'location' && (
                    <>
                      <div className="form-group">
                        <label>Latitude {venuesRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="number"
                          step="any"
                          value={venuesPayload.latitude}
                          onChange={(e) => setVenuesPayload({...venuesPayload, latitude: e.target.value})}
                          placeholder="e.g., 41.8781"
                          disabled={venuesRegionMode === 'region'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Longitude {venuesRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="number"
                          step="any"
                          value={venuesPayload.longitude}
                          onChange={(e) => setVenuesPayload({...venuesPayload, longitude: e.target.value})}
                          placeholder="e.g., -87.6298"
                          disabled={venuesRegionMode === 'region'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Radius {venuesRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="number"
                          value={venuesPayload.radius}
                          onChange={(e) => setVenuesPayload({...venuesPayload, radius: e.target.value})}
                          placeholder="e.g., 25"
                          disabled={venuesRegionMode === 'region'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Unit {venuesRegionMode === 'region' && '(from region)'}</label>
                        <select
                          value={venuesPayload.unit}
                          onChange={(e) => setVenuesPayload({...venuesPayload, unit: e.target.value})}
                          disabled={venuesRegionMode === 'region'}
                        >
                          <option value="miles">Miles</option>
                          <option value="km">Kilometers</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label>Region Name {venuesRegionMode === 'region' && '(from region)'}</label>
                    <input
                      type="text"
                      value={venuesPayload.region_name}
                      onChange={(e) => setVenuesPayload({...venuesPayload, region_name: e.target.value})}
                      placeholder="e.g., chicago"
                      disabled={venuesRegionMode === 'region'}
                    />
                  </div>
                  {venuesMode === 'location' && (
                    <div className="form-group">
                      <label>Page (optional)</label>
                      <input
                        type="number"
                        value={venuesPayload.page}
                        onChange={(e) => setVenuesPayload({...venuesPayload, page: e.target.value})}
                        placeholder="0"
                      />
                    </div>
                  )}
                </>
              )}
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={triggering}>
                  {triggering ? 'Triggering...' : 'Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events Ingestion Modal */}
      {showModal === 'events' && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trigger Ticketmaster Events Ingestion</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>
            <form onSubmit={handleEventsSubmit}>
              <div className="form-group">
                <label>Ingestion Mode</label>
                <select
                  value={eventsMode}
                  onChange={(e) => handleEventsModeChange(e.target.value as 'all_venues_by_id' | 'region_venues_by_id' | 'region_by_location')}
                >
                  <option value="all_venues_by_id">Trigger All Venues By Id</option>
                  <option value="region_venues_by_id">Trigger Region Venues By Id</option>
                  <option value="region_by_location">Trigger Region by Location</option>
                </select>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  {eventsMode === 'all_venues_by_id' && (
                    <span>Queries events for all known venues across all regions using venue IDs (most reliable).</span>
                  )}
                  {eventsMode === 'region_venues_by_id' && (
                    <span>Queries events for all known venues in a specific region using venue IDs (most reliable for a region).</span>
                  )}
                  {eventsMode === 'region_by_location' && (
                    <span>Queries events using location-based search (may miss some venues like Aragon Ballroom).</span>
                  )}
                </div>
              </div>

              {(eventsMode === 'region_venues_by_id' || eventsMode === 'region_by_location') && (
                <div className="form-group">
                  <label>Region {eventsMode === 'region_venues_by_id' ? '(required)' : '(optional - pre-fills location)'}</label>
                  <select
                    value={selectedEventsRegion}
                    onChange={(e) => handleEventsRegionChange(e.target.value)}
                    disabled={loadingRegions}
                    required={eventsMode === 'region_venues_by_id'}
                  >
                    {loadingRegions ? (
                      <option>Loading regions...</option>
                    ) : (
                      <>
                        <option value="">Select a region...</option>
                        {regions.map((region) => (
                          <option key={region.region_name} value={region.region_name}>
                            {region.metro_name} ({region.region_name})
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              )}

              {eventsMode === 'region_by_location' && (
                <>
                  <div className="form-group">
                    <label>Latitude {selectedEventsRegion && '(from region)'}</label>
                    <input
                      type="number"
                      step="any"
                      value={eventsPayload.latitude}
                      onChange={(e) => setEventsPayload({...eventsPayload, latitude: e.target.value})}
                      placeholder="e.g., 41.8781"
                      disabled={!!selectedEventsRegion}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Longitude {selectedEventsRegion && '(from region)'}</label>
                    <input
                      type="number"
                      step="any"
                      value={eventsPayload.longitude}
                      onChange={(e) => setEventsPayload({...eventsPayload, longitude: e.target.value})}
                      placeholder="e.g., -87.6298"
                      disabled={!!selectedEventsRegion}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Radius {selectedEventsRegion && '(from region)'}</label>
                    <input
                      type="number"
                      value={eventsPayload.radius}
                      onChange={(e) => setEventsPayload({...eventsPayload, radius: e.target.value})}
                      placeholder="e.g., 25"
                      disabled={!!selectedEventsRegion}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit {selectedEventsRegion && '(from region)'}</label>
                    <select
                      value={eventsPayload.unit}
                      onChange={(e) => setEventsPayload({...eventsPayload, unit: e.target.value})}
                      disabled={!!selectedEventsRegion}
                    >
                      <option value="miles">Miles</option>
                      <option value="km">Kilometers</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Date (optional)</label>
                    <input
                      type="date"
                      value={eventsPayload.start_date}
                      onChange={(e) => setEventsPayload({...eventsPayload, start_date: e.target.value})}
                      min={getTodayDate()}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date (optional)</label>
                    <input
                      type="date"
                      value={eventsPayload.end_date}
                      onChange={(e) => setEventsPayload({...eventsPayload, end_date: e.target.value})}
                      min={getTodayDate()}
                    />
                  </div>
                  <div className="form-group">
                    <label>Page (optional)</label>
                    <input
                      type="number"
                      value={eventsPayload.page}
                      onChange={(e) => setEventsPayload({...eventsPayload, page: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={triggering}>
                  {triggering ? 'Triggering...' : 'Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EventIM Events Ingestion Modal */}
      {showModal === 'events-eventim' && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trigger EventIM Events Ingestion</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>
            <form onSubmit={handleEventsEventimSubmit}>
              <div className="form-group">
                <label>Region</label>
                <select
                  value={eventsEventimRegionMode === 'region' ? selectedEventsEventimRegion : eventsEventimRegionMode}
                  onChange={(e) => handleEventsEventimRegionChange(e.target.value)}
                  disabled={loadingRegions}
                >
                  {loadingRegions ? (
                    <option>Loading regions...</option>
                  ) : (
                    <>
                      {regions.map((region) => (
                        <option key={region.region_name} value={region.region_name}>
                          {region.metro_name} ({region.region_name})
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                      <option value="empty">Empty (All Regions)</option>
                    </>
                  )}
                </select>
              </div>
              {eventsEventimRegionMode !== 'empty' && (
                <div className="form-group">
                  <label>Region Name {eventsEventimRegionMode === 'region' && '(from region)'}</label>
                  <input
                    type="text"
                    value={eventsEventimPayload.region_name}
                    onChange={(e) => setEventsEventimPayload({...eventsEventimPayload, region_name: e.target.value})}
                    placeholder="e.g., chicago"
                    disabled={eventsEventimRegionMode === 'region'}
                  />
                </div>
              )}
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={triggering}>
                  {triggering ? 'Triggering...' : 'Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events Scraper Ingestion Modal (Run one config) */}
      {showModal === 'events-scraper' && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trigger Events Scraper (One Config)</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>
            <form onSubmit={handleEventsScraperRunOneSubmit}>
              <div className="form-group">
                <label>Config</label>
                <select
                  value={selectedScraperConfig}
                  onChange={(e) => setSelectedScraperConfig(e.target.value)}
                  disabled={loadingScraperConfigs}
                >
                  {loadingScraperConfigs ? (
                    <option>Loading configs...</option>
                  ) : (
                    <>
                      <option value="">Select a config</option>
                      {scraperConfigIds.map((id) => (
                        <option key={id} value={id}>{id}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={triggering || !selectedScraperConfig}>
                  {triggering ? 'Triggering...' : 'Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Etix Events Ingestion Modal */}
      {showModal === 'events-etix' && (
        <div className="modal-overlay" onClick={() => setShowModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Trigger Etix Events Ingestion</h3>
              <button className="modal-close" onClick={() => setShowModal(null)}>×</button>
            </div>
            <form onSubmit={handleEventsEtixSubmit}>
              <div className="form-group">
                <label>Etix API Type</label>
                <select
                  value={eventsEtixPayload.etix_api_type}
                  onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, etix_api_type: e.target.value as 'both' | 'affiliate' | 'public'})}
                >
                  <option value="both">Both (Affiliate then Public)</option>
                  <option value="affiliate">Affiliate Only</option>
                  <option value="public">Public Only</option>
                </select>
              </div>
              <div className="form-group">
                <label>Region</label>
                <select
                  value={eventsEtixRegionMode === 'region' ? selectedEventsEtixRegion : eventsEtixRegionMode}
                  onChange={(e) => handleEventsEtixRegionChange(e.target.value)}
                  disabled={loadingRegions}
                >
                  {loadingRegions ? (
                    <option>Loading regions...</option>
                  ) : (
                    <>
                      {regions.map((region) => (
                        <option key={region.region_name} value={region.region_name}>
                          {region.metro_name} ({region.region_name})
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                      <option value="empty">Empty</option>
                    </>
                  )}
                </select>
              </div>
              {eventsEtixRegionMode !== 'empty' && (
                <>
                  {eventsEtixPayload.etix_api_type === 'public' ? (
                    <>
                      <div className="form-group">
                        <label>Latitude {eventsEtixRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="number"
                          step="any"
                          value={eventsEtixPayload.latitude}
                          onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, latitude: e.target.value})}
                          placeholder="e.g., 41.8781"
                          disabled={eventsEtixRegionMode === 'region'}
                        />
                      </div>
                      <div className="form-group">
                        <label>Longitude {eventsEtixRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="number"
                          step="any"
                          value={eventsEtixPayload.longitude}
                          onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, longitude: e.target.value})}
                          placeholder="e.g., -87.6298"
                          disabled={eventsEtixRegionMode === 'region'}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>Zip Code {eventsEtixRegionMode === 'region' && '(from region)'}</label>
                        <input
                          type="text"
                          value={eventsEtixPayload.zip_code}
                          onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, zip_code: e.target.value})}
                          placeholder="e.g., 60601"
                          disabled={eventsEtixRegionMode === 'region'}
                        />
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label>Radius {eventsEtixRegionMode === 'region' && '(from region)'}</label>
                    <input
                      type="number"
                      value={eventsEtixPayload.radius}
                      onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, radius: e.target.value})}
                      placeholder="e.g., 25"
                      disabled={eventsEtixRegionMode === 'region'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Page Number (optional)</label>
                    <input
                      type="number"
                      value={eventsEtixPayload.page_number}
                      onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, page_number: e.target.value})}
                      placeholder="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Region Name {eventsEtixRegionMode === 'region' && '(from region)'}</label>
                    <input
                      type="text"
                      value={eventsEtixPayload.region_name}
                      onChange={(e) => setEventsEtixPayload({...eventsEtixPayload, region_name: e.target.value})}
                      placeholder="e.g., chicago"
                      disabled={eventsEtixRegionMode === 'region'}
                    />
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="submit" className="btn-primary" disabled={triggering}>
                  {triggering ? 'Triggering...' : 'Trigger'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default IngestionPage;
